import React, { useEffect, useMemo, useState } from 'react';

import Link from '@amzn/meridian/link';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import {
  PLACEMENT_OVERVIEW_TABS,
  type PlacementOverviewTab,
} from 'src/components/pages/placementsOverview/placementOverviewTabs';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { ASSOCIATE_PROFILE_BASEURL, PLACEMENT_DETAILS_BASEURL } from 'src/constants/Routes';
import { type AllocatedJobShift } from 'src/services/placementOverview/types/allocatedJobShift';
import { type TableDataRow } from 'src/services/placementOverview/types/tableDataRow';
import {
  selectVisibleAllPlacementsTableData,
  setCurrentPage,
  setTotalPages,
} from 'src/store/placementOverviewPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import type { Nullable } from 'src/types/utilities';
import { SortStrategy } from 'src/utils/sortStrategies';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

type TableHeader = {
  sortColumn: keyof TableDataRow;
  label: string;
  condition?: boolean;
  id?: string;
};

const DEFAULT_SORT_COLUMN: keyof TableDataRow = 'associateNameOrAlias';

const getAllTableHeaders = (): TableHeader[] => [
  { sortColumn: 'associateNameOrAlias', label: 'Associate Name' },
  { sortColumn: 'caseId', label: 'Case ID' },
  { sortColumn: 'jobs', label: 'Jobs' },
  { sortColumn: 'placementStartDate', label: 'Start Date' },
  { sortColumn: 'placementEndDate', label: 'End Date' },
  { sortColumn: 'status', label: 'Status' },
];

// Fields All Statuses renders, including values shown inside composite cells rather than their own column
const ALL_STATUSES_SEARCHABLE_FIELDS: (keyof TableDataRow)[] = [
  'associateNameOrAlias',
  'associateFullName',
  'alias',
  'originalShiftCode',
  'caseId',
  'placementType',
  'jobs',
  'jobTypes',
  'shiftCodes',
  'jobSuffix',
  'placementStartDate',
  'placementEndDate',
  'status',
];

const getStatusTableHeaders = (
  daysInCategoryTableHeader: string,
  canShow180DayCounter: boolean,
  isClosedPlacementsTab: boolean,
): TableHeader[] => [
  { sortColumn: 'associateNameOrAlias', label: 'Associate Name' },
  { sortColumn: 'caseId', label: 'Case ID' },
  { sortColumn: 'jobs', label: 'Jobs' },
  { sortColumn: 'placementStartDate', label: 'Start Date' },
  { sortColumn: 'placementEndDate', label: 'End Date' },
  { sortColumn: 'daysInCategory', label: `${daysInCategoryTableHeader} (days)` },
  {
    sortColumn: 'totalAccommodatedDaysByAssociate',
    label: 'Total Days in Placement(s)',
    condition: canShow180DayCounter,
    id: 'placementOverview__table__totalDaysInPlacement',
  },
  {
    sortColumn: 'closedAtDate',
    label: 'Closed At Date',
    condition: isClosedPlacementsTab,
  },
  { sortColumn: 'status', label: 'Status' },
];

const getTransferredInTableHeaders = (): TableHeader[] => [
  { sortColumn: 'associateNameOrAlias', label: 'Associate Name' },
  { sortColumn: 'caseId', label: 'Case ID' },
  { sortColumn: 'previousSite', label: 'Transferred From Site' },
  { sortColumn: 'transferredAtDate', label: 'Placement Transfer Date' },
  {
    sortColumn: 'daysInCategory',
    label: 'Time in Transferred Status (days)',
  },
  { sortColumn: 'status', label: 'Status' },
];

const evaluateSortStrategy = (sortColumn: string) => {
  const columnStrategies = {
    placementStartDate: SortStrategy.DATE,
    placementEndDate: SortStrategy.DATE,
    closedAtDate: SortStrategy.DATE,
    transferredAtDate: SortStrategy.DATE,
    daysInCategory: SortStrategy.NUMBER,
    totalAccommodatedDaysByAssociate: SortStrategy.NUMBER,
  };

  return columnStrategies[sortColumn as keyof typeof columnStrategies] || SortStrategy.DEFAULT;
};

const PlacementsOverviewTable = ({ currentTab }: { currentTab: PlacementOverviewTab }) => {
  const dispatch = useAppDispatch();
  const allPlacementsTableData = useAppSelector(selectVisibleAllPlacementsTableData);
  const {
    activePlacementsTableData,
    pendingPlacementsTableData,
    expiredPlacementsTableData,
    closedPlacementsTableData,
    transferredInPlacementsTableData,
    searchField,
    currentPage,
    perPage,
  } = useAppSelector((state) => state.placementOverviewPage);
  const { selectedSite } = useAppSelector((state) => state.sites);
  const { featureFlags } = useAppSelector((state) => state.user);

  const [tableData, setTableData] = useState<TableDataRow[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof TableDataRow>(DEFAULT_SORT_COLUMN);
  const [sortStrategy, setSortStrategy] = useState(SortStrategy.DEFAULT);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const [daysInCategoryTableHeader, setDaysInCategoryTableHeader] = useState('');
  const isAllPlacementsTab = currentTab === PLACEMENT_OVERVIEW_TABS.ALL_STATUSES;
  const isTransferredInTab = currentTab === PLACEMENT_OVERVIEW_TABS.TRANSFERRED_IN;

  const onSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: keyof TableDataRow;
    sortDirection: TableSortDirection;
  }) => {
    const sortStrategy = evaluateSortStrategy(sortColumn);
    setSortStrategy(sortStrategy);
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  const canShow180DayCounter = useMemo(() => {
    return (
      currentTab !== PLACEMENT_OVERVIEW_TABS.CLOSED && selectedSite.countryId !== COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID
    );
  }, [currentTab, selectedSite.countryId]);

  useEffect(() => {
    switch (currentTab) {
      case PLACEMENT_OVERVIEW_TABS.ALL_STATUSES:
        setTableData(allPlacementsTableData);
        break;
      case PLACEMENT_OVERVIEW_TABS.PENDING_DECISIONS:
        setTableData(pendingPlacementsTableData);
        setDaysInCategoryTableHeader('Dwell Time');
        break;
      case PLACEMENT_OVERVIEW_TABS.OVERDUE:
        setTableData(expiredPlacementsTableData);
        setDaysInCategoryTableHeader('Time Expired');
        break;
      case PLACEMENT_OVERVIEW_TABS.IN_PROGRESS:
        setTableData(activePlacementsTableData);
        setDaysInCategoryTableHeader('Time in Placement');
        break;
      case PLACEMENT_OVERVIEW_TABS.CLOSED:
        setTableData(closedPlacementsTableData);
        setDaysInCategoryTableHeader('Time Closed');
        break;
      case PLACEMENT_OVERVIEW_TABS.TRANSFERRED_IN:
        setTableData(transferredInPlacementsTableData);
        break;
    }
  }, [
    currentTab,
    allPlacementsTableData,
    activePlacementsTableData,
    pendingPlacementsTableData,
    expiredPlacementsTableData,
    closedPlacementsTableData,
    transferredInPlacementsTableData,
  ]);

  const { paginatedData, totalPages } = useMemo(() => {
    const jobsNotDisplayed = (row: any) => !row.jobsDisplayed;
    // Search only what each tab shows: All Statuses allowlists its fields and the others skip hidden ones
    const columnsToSkip = isAllPlacementsTab
      ? Object.keys(tableData[0] ?? {}).filter(
          (column) => !ALL_STATUSES_SEARCHABLE_FIELDS.includes(column as keyof TableDataRow),
        )
      : isTransferredInTab
        ? ['id', 'requesterLogin', 'placementStartDate', 'placementEndDate']
        : ['id', 'requesterLogin'];
    return filterSortAndPaginateTableData(
      tableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      columnsToSkip,
      perPage,
      sortStrategy,
      [
        ['jobs', jobsNotDisplayed],
        ['jobTypes', jobsNotDisplayed],
        ['shiftCodes', jobsNotDisplayed],
      ],
    );
  }, [sortColumn, sortDirection, tableData, searchField, currentPage, perPage, isAllPlacementsTab, isTransferredInTab]);

  useEffect(() => {
    dispatch(setTotalPages(totalPages));
  }, [totalPages]);

  useEffect(() => {
    // Reset pagination when local table controls change
    dispatch(setCurrentPage(1));
  }, [searchField, sortColumn, sortDirection, perPage, dispatch]);

  const tableHeaders = useMemo(
    () =>
      isAllPlacementsTab
        ? getAllTableHeaders()
        : isTransferredInTab
          ? getTransferredInTableHeaders()
          : getStatusTableHeaders(
              daysInCategoryTableHeader,
              canShow180DayCounter,
              currentTab === PLACEMENT_OVERVIEW_TABS.CLOSED,
            ),
    [isAllPlacementsTab, isTransferredInTab, daysInCategoryTableHeader, canShow180DayCounter, currentTab],
  );

  // Sorting is local state, so drop it when the active column is not part of the current tab's schema
  useEffect(() => {
    const isSortColumnVisible = tableHeaders.some(
      (header) => header.condition !== false && header.sortColumn === sortColumn,
    );
    if (!isSortColumnVisible) {
      setSortColumn(DEFAULT_SORT_COLUMN);
      setSortStrategy(SortStrategy.DEFAULT);
      setSortDirection('ascending');
    }
  }, [tableHeaders, sortColumn]);

  const handleJobs = (
    jobs: (string | null)[],
    jobTypes: (string | null)[],
    shiftCodes: (string | null)[],
    jobSuffix: string,
    hasMultipleJobs: boolean,
  ) =>
    !hasMultipleJobs ? (
      <>
        <Text type="b300">
          {jobs[0]} {jobSuffix}
        </Text>
        <Text type="b200" color="secondary">
          {jobTypes[0] ?? '-'}
        </Text>
        <Text type="b200" color="secondary">
          {shiftCodes[0] ?? '-'}
        </Text>
      </>
    ) : (
      `${jobs.length} jobs`
    );

  const visibleHeaderCount = tableHeaders.filter((h) => h.condition ?? true).length;
  const nestedRowLeadingCellCount = 3;

  const handleAllocatedJobShifts = (
    allocatedJobShifts: AllocatedJobShift[],
    jobs: (string | null)[],
    jobTypes: (string | null)[],
    shiftCodes: (string | null)[],
    jobSuffix: string,
  ) =>
    allocatedJobShifts.length > 1 &&
    allocatedJobShifts.map((_: AllocatedJobShift, index: number) => (
      <TableRow key={index} backgroundColor="secondary">
        <TableCell />
        <TableCell />
        <TableCell>
          <Text type="b300">
            {jobs[index]} {jobSuffix}
          </Text>
          <Text type="b200" color="secondary">
            {jobTypes[index] ?? ''}
          </Text>
          <Text color="secondary">{shiftCodes[index] ?? ''}</Text>
        </TableCell>
        {Array.from({ length: visibleHeaderCount - nestedRowLeadingCellCount }, (_, index) => (
          <TableCell key={`nested-row-filler-${index}`} />
        ))}
      </TableRow>
    ));

  const renderCaseIdCell = (placementId: number, caseId: string, placementType: string | null) => {
    return (
      <>
        <Text type="b300">
          <Link type="secondary" href={`${PLACEMENT_DETAILS_BASEURL}/${placementId}`}>
            {caseId ?? '[Case ID Missing]'}
          </Link>
        </Text>
        <Text type="b200" color="secondary">
          {placementType ?? ''}
        </Text>
      </>
    );
  };

  const renderSortedTableData = () =>
    paginatedData.map(
      ({
        alias,
        allocatedJobShifts,
        associateFullName,
        caseId,
        category,
        closedAtDate,
        daysInCategory,
        id,
        jobs,
        jobTypes,
        originalShiftCode,
        placementStartDate,
        placementEndDate,
        placementType,
        shiftCodes,
        status,
        totalAccommodatedDaysByAssociate,
        jobsDisplayed,
        jobSuffix,
        previousSite,
        transferredAtDate,
      }) => {
        const caseIdCell = renderCaseIdCell(id, caseId, placementType);
        const hasMultipleJobs = allocatedJobShifts.length > 1;

        return !isTransferredInTab ? (
          <UncontrolledExpandableRow key={id}>
            <TableCell>
              <EmployeeCell
                {...{ associateFullName, alias, shiftCode: originalShiftCode, personaEnabled: featureFlags.PERSONA }}
              />
            </TableCell>
            <TableCell>{caseIdCell}</TableCell>
            <TableCell>{jobsDisplayed && handleJobs(jobs, jobTypes, shiftCodes, jobSuffix, hasMultipleJobs)}</TableCell>
            <TableCell>{placementStartDate}</TableCell>
            <TableCell>{placementEndDate}</TableCell>
            {!isAllPlacementsTab && <TableCell>{daysInCategory}</TableCell>}
            {!isAllPlacementsTab && canShow180DayCounter && <TableCell>{totalAccommodatedDaysByAssociate}</TableCell>}
            {currentTab === PLACEMENT_OVERVIEW_TABS.CLOSED && <TableCell>{closedAtDate}</TableCell>}
            <TableCell>
              <Tag type={category === 'Active' ? 'success' : 'neutral'}>{status}</Tag>
            </TableCell>
            {jobsDisplayed && handleAllocatedJobShifts(allocatedJobShifts, jobs, jobTypes, shiftCodes, jobSuffix)}
          </UncontrolledExpandableRow>
        ) : (
          <UncontrolledExpandableRow key={id}>
            <TableCell>
              <EmployeeCell
                {...{ associateFullName, alias, shiftCode: originalShiftCode, personaEnabled: featureFlags.PERSONA }}
              />
            </TableCell>
            <TableCell>{caseIdCell}</TableCell>
            <TableCell>{previousSite}</TableCell>
            <TableCell>{transferredAtDate}</TableCell>
            <TableCell>{daysInCategory}</TableCell>
            <TableCell>
              <Tag type="neutral">{status}</Tag>
            </TableCell>
          </UncontrolledExpandableRow>
        );
      },
    );

  return (
    <Table
      headerRows={1}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={onSort}
      showDividers
      showExpandedDividers
      fixHeaderRows
      rowComponents={[UncontrolledExpandableRow, TableRow]}
      nestedRows
    >
      <TableRow>
        {tableHeaders
          .filter((header) => header.condition !== false)
          .map((header) => (
            <TableCell key={header.sortColumn} sortColumn={header.sortColumn} id={header.id}>
              {header.label}
            </TableCell>
          ))}
      </TableRow>
      {renderSortedTableData()}
    </Table>
  );
};

export interface EmployeeCellProps {
  associateFullName: Nullable<string>;
  alias: Nullable<string>;
  shiftCode: Nullable<string>;
  personaEnabled: boolean;
}

export const EmployeeCell = (props: EmployeeCellProps) => {
  const { alias, associateFullName, personaEnabled, shiftCode } = props;

  const nameFragment =
    personaEnabled && alias && associateFullName ? (
      <Link href={`${ASSOCIATE_PROFILE_BASEURL}/${alias}`} type="secondary">
        {associateFullName}
      </Link>
    ) : (
      // missing one or both requirements for profile link. Display plain text if name is present
      associateFullName && <Text type="b300">{associateFullName}</Text>
    );

  return (
    <>
      {nameFragment}
      <AliasText {...props} />
      {shiftCode && (
        <Text type="b200" color="secondary">
          {shiftCode}
        </Text>
      )}
    </>
  );
};

/** Alias has 4 different display states depending on persona being enabled and the various degrees of missing information.
 * This component encapsulates that complexity */
export const AliasText = ({ alias, associateFullName, personaEnabled, shiftCode: _ }: EmployeeCellProps) => {
  if (!alias) {
    return (
      <Text type="b300" color="error">
        [MISSING ALIAS]
      </Text>
    );
  } else if (personaEnabled && !associateFullName) {
    return (
      <Link href={`${ASSOCIATE_PROFILE_BASEURL}/${alias}`} type="secondary">
        {alias}@
      </Link>
    );
  } else {
    return (
      <Text type={associateFullName ? 'b200' : 'b300'} color={associateFullName ? 'secondary' : 'primary'}>
        {alias}@
      </Text>
    );
  }
};

export default PlacementsOverviewTable;
